import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { MinesService } from '../mines.service';
import { DecimalPipe } from '@angular/common';
import { BalanceIconComponent } from '@core/components/balance-icon/balance-icon.component';
import { NgParticlesService, NgxParticlesModule } from '@tsparticles/angular';
import { Engine, ISourceOptions } from '@tsparticles/engine';
import { loadSlim } from '@tsparticles/slim';
import { UIService } from '@core/services/ui.service';

@Component({
  selector: 'app-mines-tile',
  templateUrl: './mines-tile.component.html',
  styleUrls: ['./mines-tile.component.scss'],
  standalone: true,
  imports: [BalanceIconComponent, DecimalPipe, NgxParticlesModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MinesTileComponent {
  public readonly minesService = inject(MinesService);
  private readonly ngParticlesService = inject(NgParticlesService);
  public readonly uiService = inject(UIService)

  readonly isOpened = input.required<boolean>();
  readonly isBomb = input.required<boolean>();
  readonly opacityEffect = input.required<boolean>();
  readonly tile = input.required<number>();

  particlesOptionsRed: ISourceOptions = {
    fullScreen: { enable: true },
    particles: {
      number: {
        value: 6,
      },
      density: {
        enable: false,
        value_area: 150,
      },
      color: {
        value: 'rgba(251, 88, 88, 1)',
      },
      move: {
        enable: true,
        speed: 1,
        direction: 'none',
        random: true,
        straight: false,
        attract: {
          enable: false,
        },
      },
      opacity: {
        value: { min: 0.36, max: 1 },
      },
      size: {
        value: { min: 0.5, max: 1.5 },
      },
      wobble: {
        enable: false,
      },
    },
  };

  particlesOptionsYellow: ISourceOptions = {
    fullScreen: { enable: true },
    particles: {
      number: {
        value: 6,
      },
      density: {
        enable: false,
        value_area: 150,
      },
      color: {
        value: ['rgb(255, 170, 0)'],
      },
      move: {
        enable: true,
        speed: 1,
        direction: 'none',
        random: true,
        straight: false,
        attract: {
          enable: false,
        },
      },
      opacity: {
        value: { min: 0.36, max: 1 },
      },
      size: {
        value: { min: 0.5, max: 1.5 },
      },
      wobble: {
        enable: false,
      },
    },
  };

  public tileProfit = computed(() => {
    const currentTile = this.tile();
    const clickedTiles = this.minesService.clicked();
    const currentGame = this.minesService.currentGame();

    if (!clickedTiles.includes(currentTile)) {
      return 0;
    }

    const clickIndex = clickedTiles.indexOf(currentTile);

    if (clickIndex === -1) {
      return 0;
    }

    if (!currentGame.grid_size || !currentGame.mines) {
      return 0;
    }

    const currentMultiplier = this.minesService.calculateTileMultiplier(
      currentGame.grid_size,
      currentGame.mines,
      clickIndex
    );

    const previousMultiplier =
      clickIndex === 0
        ? 1
        : this.minesService.calculateTileMultiplier(
            currentGame.grid_size,
            currentGame.mines,
            clickIndex - 1
          );

    const profit = currentMultiplier - previousMultiplier;

    return profit;
  });

  ngOnInit() {
    this.ngParticlesService.init(async (engine: Engine) => {
      await loadSlim(engine);
    });
  }
}
